import {
  Checkbox,
  Chip,
  UpdateIconButton,
  formatDate,
  formatMinutes,
  Grid,
  ListMenu,
  LockedIcon,
  pn,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@compass/core";

import { ListBeProperty } from "components";
import {
  LkStudentServiceDisplayFieldId,
  Permission,
  PermissionAction,
  SortObject,
  StudentServiceListDto,
  StudentServiceListResource,
} from "generated/openApi";

import QrCodeIcon from "@mui/icons-material/QrCode";
import EscalatorWarningIcon from "@mui/icons-material/EscalatorWarning";
import { useNavigate } from "react-router-dom";
import { isAuthorized } from "services";
import { studentServicePageRoute } from "../StudentServicePage/StudentServicePage";
import { useStudentServiceListMenuItems } from "./useStudentServiceListMenuItems";

export const StudentServiceTable = (props: {
  rootPermission: Permission;
  tableContainerRef: React.RefObject<HTMLDivElement>;
  studentServices: StudentServiceListDto[];
  listTotal: number | undefined;
  resource: StudentServiceListResource;
  searchFocused: boolean;
  sortObjects: SortObject[] | undefined;
  sort: (sortObjects: SortObject[]) => void;
  selectId: (id: number) => void;
  selectedIds: number[];
  setSelectedIds: React.Dispatch<React.SetStateAction<number[]>>;
  getIdsPromise: () => Promise<any>;
  loadList: () => void;
}) => {
  const navigate = useNavigate();
  const properties = props.resource.properties || [];
  const menuItems = useStudentServiceListMenuItems({
    permission: props.rootPermission,
    setSelectedIds: props.setSelectedIds,
    loadList: props.loadList,
  });

  const displayFields = props.resource.displayFields || [];
  const durationDisplayField = displayFields.find((x) => x.id === LkStudentServiceDisplayFieldId.Duration);

  return (
    <TableContainer ref={props.tableContainerRef}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            {menuItems.length > 0 && (
              <TableCell type="select">
                <ListMenu
                  selectedIds={props.selectedIds}
                  setSelectedIds={props.setSelectedIds}
                  listTotal={props.listTotal}
                  fetchFunction={() => props.getIdsPromise()}
                  actionMenuItems={menuItems}
                />
              </TableCell>
            )}
            <TableCell>
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<StudentServiceListDto>("date"))}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
                searchFocused={props.searchFocused}
              />
            </TableCell>
            <TableCell>
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<StudentServiceListDto>("serviceType"))}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
                searchFocused={props.searchFocused}
              />
            </TableCell>
            <TableCell>
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<StudentServiceListDto>("serviceSubType"))}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
                searchFocused={props.searchFocused}
              />
            </TableCell>
            <TableCell>
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<StudentServiceListDto>("serviceName"))}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
                searchFocused={props.searchFocused}
              />
            </TableCell>
            <TableCell>
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<StudentServiceListDto>("student"))}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
                searchFocused={props.searchFocused}
              />
            </TableCell>
            {durationDisplayField && (
              <TableCell>
                <ListBeProperty
                  listProperty={properties.find(
                    (x) => x.propertyName === pn<StudentServiceListDto>("durationInMinutes")
                  )}
                  sortObjects={props.sortObjects}
                  onChange={(sortObjects) => props.sort(sortObjects)}
                  searchFocused={props.searchFocused}
                />
              </TableCell>
            )}
            <TableCell align="right">
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<StudentServiceListDto>("files"))}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
                searchFocused={props.searchFocused}
              />
            </TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {props.studentServices.map((studentServiceItem) => (
            <TableRow
              key={studentServiceItem.id}
              onClick={() =>
                navigate(
                  studentServicePageRoute.getPath({
                    id: studentServiceItem.id,
                    crudMode: "view",
                    screenType: "standard",
                  })
                )
              }
            >
              {menuItems.length > 0 && (
                <TableCell type="select">
                  <Stack direction="row" spacing="sm" alignItems="center">
                    <Checkbox
                      checked={props.selectedIds.includes(studentServiceItem.id)}
                      onClick={(e) => props.selectId(studentServiceItem.id)}
                    />
                    {studentServiceItem.isFromQrCode && (<QrCodeIcon />)}
                  </Stack>
                </TableCell>
              )}
              <TableCell>{formatDate(studentServiceItem.date)}</TableCell>
              <TableCell>
                <Stack direction="row" spacing="sm" alignItems="center">
                  <Typography variant="inherit">{studentServiceItem.serviceType}</Typography>
                  {studentServiceItem?.isForParent && <EscalatorWarningIcon />}
                </Stack>
              </TableCell>
              <TableCell>{studentServiceItem.serviceSubType}</TableCell>
              <TableCell>{studentServiceItem.serviceName}</TableCell>
              <TableCell>
                {studentServiceItem.student} <Typography variant="caption">({studentServiceItem.customId1})</Typography>
              </TableCell>
              {durationDisplayField && <TableCell>{formatMinutes(studentServiceItem.durationInMinutes)}</TableCell>}
              <TableCell align="right">{studentServiceItem.files > 0 && studentServiceItem.files}</TableCell>
              <TableCell type="action">
                {studentServiceItem.isLocked === true && <LockedIcon />}

                {isAuthorized(props.rootPermission, PermissionAction.Update) &&
                  (!studentServiceItem.isLocked || isAuthorized(props.rootPermission, PermissionAction.Lock)) && (
                    <UpdateIconButton
                      onClick={() =>
                        navigate(
                          studentServicePageRoute.getPath({
                            id: studentServiceItem.id,
                            crudMode: "edit",
                            screenType: "standard",
                          })
                        )
                      }
                    />
                  )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
